import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import Card from '../components/Card';
import Alert from '../components/Alert';
import { trackEvent } from '../config/analytics';

// Tier limits for scheduled audits per week
const TIER_LIMITS = {
  small: { auditsPerWeek: 1, maxPages: 10, name: 'Small Business' },
  standard: { auditsPerWeek: 3, maxPages: 100, name: 'Standard' },
  full: { auditsPerWeek: 7, maxPages: 1000, name: 'Full Enterprise' },
  enterprise: { auditsPerWeek: 7, maxPages: 1000, name: 'Enterprise' }
};

const DAYS_OF_WEEK = [
  { id: 0, name: 'Sunday', short: 'Sun' },
  { id: 1, name: 'Monday', short: 'Mon' },
  { id: 2, name: 'Tuesday', short: 'Tue' },
  { id: 3, name: 'Wednesday', short: 'Wed' },
  { id: 4, name: 'Thursday', short: 'Thu' },
  { id: 5, name: 'Friday', short: 'Fri' },
  { id: 6, name: 'Saturday', short: 'Sat' }
];

const SchedulePage = () => {
  const { currentUser, userData, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [websites, setWebsites] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [newWebsiteUrl, setNewWebsiteUrl] = useState('');
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [preferredTime, setPreferredTime] = useState('09:00');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Get tier limits
  const tierLimits = isAdmin
    ? { auditsPerWeek: 999, maxPages: 1000, name: 'Admin (Unlimited)' }
    : TIER_LIMITS[userData?.subscriptionTier] || { auditsPerWeek: 0, maxPages: 0, name: 'No Plan' };

  // Load existing schedule
  useEffect(() => {
    const loadSchedule = async () => {
      if (!currentUser) return;

      try {
        // Load user's scheduled websites
        const schedulesQuery = query(
          collection(db, 'scheduledAudits'),
          where('userId', '==', currentUser.uid)
        );
        const snapshot = await getDocs(schedulesQuery);

        const loadedWebsites = [];
        snapshot.forEach(doc => {
          loadedWebsites.push({ id: doc.id, ...doc.data() });
        });

        setWebsites(loadedWebsites);

        // If there's at least one website, select it
        if (loadedWebsites.length > 0) {
          setSelectedWebsite(loadedWebsites[0]);
          setSelectedDays(loadedWebsites[0].scheduledDays || []);
          setPreferredTime(loadedWebsites[0].preferredTime || '09:00');
        }

        trackEvent('schedule_page_viewed');
      } catch (err) {
        console.error('Error loading schedule:', err);
        setError('Failed to load schedule');
      } finally {
        setLoading(false);
      }
    };

    loadSchedule();
  }, [currentUser]);

  const validateUrl = (urlString) => {
    try {
      const urlObj = new URL(urlString);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleAddWebsite = async () => {
    if (!newWebsiteUrl) {
      setError('Please enter a URL');
      return;
    }

    if (!validateUrl(newWebsiteUrl)) {
      setError('Please enter a valid URL (including http:// or https://)');
      return;
    }

    // Check if website already exists
    if (websites.some(w => w.url.toLowerCase() === newWebsiteUrl.toLowerCase())) {
      setError('This website is already in your schedule');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const newSchedule = {
        userId: currentUser.uid,
        url: newWebsiteUrl,
        scheduledDays: [],
        preferredTime: '09:00',
        maxPages: tierLimits.maxPages,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = doc(collection(db, 'scheduledAudits'));
      await setDoc(docRef, newSchedule);

      const newWebsite = { id: docRef.id, ...newSchedule };
      setWebsites([...websites, newWebsite]);
      setSelectedWebsite(newWebsite);
      setSelectedDays([]);
      setNewWebsiteUrl('');
      setSuccess('Website added! Now select which days to run audits.');

      trackEvent('scheduled_website_added', { url: newWebsiteUrl });
    } catch (err) {
      console.error('Error adding website:', err);
      setError('Failed to add website');
    } finally {
      setSaving(false);
    }
  };

  const handleDayToggle = (dayId) => {
    const currentCount = selectedDays.length;
    const isSelected = selectedDays.includes(dayId);

    if (isSelected) {
      // Always allow removing
      setSelectedDays(selectedDays.filter(d => d !== dayId));
    } else {
      // Check limit before adding
      if (currentCount >= tierLimits.auditsPerWeek) {
        setError(`Your ${tierLimits.name} plan allows ${tierLimits.auditsPerWeek} audit${tierLimits.auditsPerWeek > 1 ? 's' : ''} per week`);
        return;
      }
      setSelectedDays([...selectedDays, dayId]);
    }
    setError('');
  };

  const handleSaveSchedule = async () => {
    if (!selectedWebsite) {
      setError('Please select a website first');
      return;
    }

    if (selectedDays.length === 0) {
      setError('Please select at least one day');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const updatedSchedule = {
        ...selectedWebsite,
        scheduledDays: selectedDays,
        preferredTime: preferredTime,
        updatedAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'scheduledAudits', selectedWebsite.id), updatedSchedule);

      // Update local state
      setWebsites(websites.map(w =>
        w.id === selectedWebsite.id ? updatedSchedule : w
      ));
      setSelectedWebsite(updatedSchedule);
      setSuccess('Schedule saved successfully!');

      trackEvent('schedule_saved', {
        url: selectedWebsite.url,
        days: selectedDays.length
      });
    } catch (err) {
      console.error('Error saving schedule:', err);
      setError('Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWebsite = async (websiteId) => {
    if (!window.confirm('Are you sure you want to remove this website from scheduled audits?')) {
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, 'scheduledAudits', websiteId), { deleted: true }, { merge: true });

      const updatedWebsites = websites.filter(w => w.id !== websiteId);
      setWebsites(updatedWebsites);

      if (selectedWebsite?.id === websiteId) {
        setSelectedWebsite(updatedWebsites[0] || null);
        setSelectedDays(updatedWebsites[0]?.scheduledDays || []);
      }

      setSuccess('Website removed from schedule');
      trackEvent('scheduled_website_removed');
    } catch (err) {
      console.error('Error deleting website:', err);
      setError('Failed to remove website');
    } finally {
      setSaving(false);
    }
  };

  const handleWebsiteSelect = (website) => {
    setSelectedWebsite(website);
    setSelectedDays(website.scheduledDays || []);
    setPreferredTime(website.preferredTime || '09:00');
    setError('');
    setSuccess('');
  };

  // Check subscription
  const hasValidSubscription = isAdmin || userData?.subscriptionStatus === 'active';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading schedule...</p>
        </div>
      </div>
    );
  }

  if (!hasValidSubscription) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4">
          <Card className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Subscription Required</h2>
            <p className="text-gray-600 mb-6">
              Scheduled audits are available with an active subscription plan.
            </p>
            <Button onClick={() => navigate('/subscription')}>
              View Plans
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Scheduled Audits</h1>
          <p className="mt-2 text-gray-600">
            Set up automatic accessibility audits for your websites
          </p>
        </div>

        {/* Plan Info */}
        <Card className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-700">Your Plan</h3>
              <p className="text-xl font-bold text-indigo-600 mt-1">{tierLimits.name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{tierLimits.auditsPerWeek}</span> audit{tierLimits.auditsPerWeek > 1 ? 's' : ''}/week
              </p>
              <p className="text-sm text-gray-600">
                Up to <span className="font-semibold text-gray-900">{tierLimits.maxPages}</span> pages/audit
              </p>
            </div>
          </div>
        </Card>

        {error && <Alert type="error" message={error} onClose={() => setError('')} className="mb-4" />}
        {success && <Alert type="success" message={success} onClose={() => setSuccess('')} className="mb-4" />}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Website List */}
          <div className="lg:col-span-1">
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Websites</h3>

              {/* Add Website Form */}
              <div className="mb-4">
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={newWebsiteUrl}
                    onChange={(e) => setNewWebsiteUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <Button
                    onClick={handleAddWebsite}
                    loading={saving}
                    size="sm"
                  >
                    Add
                  </Button>
                </div>
              </div>

              {/* Website List */}
              {websites.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No websites added yet. Add your first website above.
                </p>
              ) : (
                <div className="space-y-2">
                  {websites.map(website => (
                    <div
                      key={website.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedWebsite?.id === website.id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleWebsiteSelect(website)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {website.url.replace(/^https?:\/\//, '')}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {website.scheduledDays?.length || 0} day{website.scheduledDays?.length !== 1 ? 's' : ''} scheduled
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteWebsite(website.id);
                          }}
                          className="text-gray-400 hover:text-red-500 ml-2"
                          title="Remove website"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Schedule Configuration */}
          <div className="lg:col-span-2">
            <Card>
              {selectedWebsite ? (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Schedule for {selectedWebsite.url.replace(/^https?:\/\//, '')}
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Select the days you want automatic audits to run. You can select up to {tierLimits.auditsPerWeek} day{tierLimits.auditsPerWeek > 1 ? 's' : ''} per week.
                  </p>

                  {/* Day Selector */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Audit Days ({selectedDays.length}/{tierLimits.auditsPerWeek} selected)
                    </label>
                    <div className="grid grid-cols-7 gap-2">
                      {DAYS_OF_WEEK.map(day => {
                        const isSelected = selectedDays.includes(day.id);
                        const isDisabled = !isSelected && selectedDays.length >= tierLimits.auditsPerWeek;

                        return (
                          <button
                            key={day.id}
                            onClick={() => handleDayToggle(day.id)}
                            disabled={isDisabled}
                            className={`
                              p-3 rounded-lg border-2 text-center transition-all
                              ${isSelected
                                ? 'border-indigo-600 bg-indigo-600 text-white'
                                : isDisabled
                                  ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'border-gray-200 hover:border-indigo-300 text-gray-700'
                              }
                            `}
                          >
                            <span className="block text-xs font-medium">{day.short}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Time Selector */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Time (UTC)
                    </label>
                    <select
                      value={preferredTime}
                      onChange={(e) => setPreferredTime(e.target.value)}
                      className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {Array.from({ length: 24 }, (_, i) => {
                        const hour = i.toString().padStart(2, '0');
                        return (
                          <option key={i} value={`${hour}:00`}>
                            {hour}:00 UTC
                          </option>
                        );
                      })}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Audits will run around this time on selected days
                    </p>
                  </div>

                  {/* Summary */}
                  {selectedDays.length > 0 && (
                    <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-6">
                      <h4 className="font-medium text-indigo-900 mb-2">Schedule Summary</h4>
                      <p className="text-sm text-indigo-800">
                        <span className="font-semibold">{selectedWebsite.url.replace(/^https?:\/\//, '')}</span> will be audited every{' '}
                        <span className="font-semibold">
                          {selectedDays
                            .sort((a, b) => a - b)
                            .map(d => DAYS_OF_WEEK.find(day => day.id === d)?.name)
                            .join(', ')}
                        </span>{' '}
                        at <span className="font-semibold">{preferredTime} UTC</span>
                      </p>
                    </div>
                  )}

                  {/* Save Button */}
                  <div className="flex gap-4">
                    <Button
                      onClick={handleSaveSchedule}
                      loading={saving}
                      disabled={selectedDays.length === 0}
                    >
                      Save Schedule
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => navigate('/audit/new')}
                    >
                      Run Audit Now
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Website Selected</h3>
                  <p className="text-gray-600">
                    Add a website on the left to set up scheduled audits
                  </p>
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8">
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">How Scheduled Audits Work</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-3">
                  <span className="text-indigo-600 font-bold">1</span>
                </div>
                <h4 className="font-medium text-gray-900 mb-1">Add Your Website</h4>
                <p className="text-sm text-gray-600">
                  Enter your website URL to add it to your monitoring list
                </p>
              </div>
              <div>
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-3">
                  <span className="text-indigo-600 font-bold">2</span>
                </div>
                <h4 className="font-medium text-gray-900 mb-1">Pick Your Days</h4>
                <p className="text-sm text-gray-600">
                  Select which days you want audits to run automatically
                </p>
              </div>
              <div>
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-3">
                  <span className="text-indigo-600 font-bold">3</span>
                </div>
                <h4 className="font-medium text-gray-900 mb-1">Get Reports</h4>
                <p className="text-sm text-gray-600">
                  Receive email reports with accessibility findings each audit day
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SchedulePage;
